// PRO+ hub sections in the Volt look: About, Watch, Press, Book.
import { useState } from "react";
import { Instagram, Facebook, Mail, Play, ExternalLink, Quote } from "lucide-react";
import { embedSrcFrom } from "../blocks/media";
import { Eyebrow, FadeIn } from "../voltbox/ui";
import { BandDiscography, BandMembers } from "./BandStage";
import { NEON, ytId } from "./hubApi";

export const HubHead = ({ eyebrow, title, sub }) => (
  <FadeIn className="mb-6">
    <Eyebrow color={NEON} className="mb-2">{eyebrow}</Eyebrow>
    <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold leading-tight text-white m-0">{title}</h2>
    {sub && <p className="mt-2 text-sm text-white/55 max-w-2xl">{sub}</p>}
  </FadeIn>
);

export const HubAbout = ({ band }) => (
  <section id="about" className="scroll-mt-24 mb-16" data-testid="hub-about">
    <HubHead eyebrow="About" title={`Meet ${band.name}`} />
    {band.bio && <p className="text-lg sm:text-xl leading-relaxed text-white/85 max-w-3xl mb-10 whitespace-pre-line" data-testid="hub-bio">{band.bio}</p>}
    <BandMembers members={band.members || []} />
    <BandDiscography records={band.discography || []} />
  </section>
);

const VideoTile = ({ v, i, big }) => {
  const [play, setPlay] = useState(false);
  const id = ytId(v.url);
  const src = embedSrcFrom(v.url);
  return (
    <figure className={`m-0 ${big ? "md:col-span-2" : ""}`} data-testid={`hub-video-${i}`}>
      <div className="relative aspect-video rounded-2xl overflow-hidden bg-[#0A0F14] ring-1 ring-[#54E3FF]/25">
        {id && !play ? (
          <button type="button" onClick={() => setPlay(true)} className="group absolute inset-0 w-full" aria-label={`Play ${v.title || "video"}`} data-testid={`hub-video-play-${i}`}>
            <img src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`} alt="" loading="lazy" className="h-full w-full object-cover opacity-75 group-hover:opacity-100 transition-opacity duration-500" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="h-16 w-16 rounded-full bg-[#54E3FF] text-[#030608] flex items-center justify-center shadow-[0_0_40px_rgba(84,227,255,0.6)] group-hover:scale-110 transition-transform"><Play className="h-7 w-7 ml-1" fill="currentColor" /></span>
            </span>
          </button>
        ) : (
          <iframe src={id ? `${src}${src.includes("?") ? "&" : "?"}autoplay=1` : src} title={v.title || "Video"} className="absolute inset-0 h-full w-full" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen data-testid={`hub-video-frame-${i}`} />
        )}
      </div>
      {v.title && <figcaption className="mt-2.5 text-sm font-bold text-white/85">{v.title}</figcaption>}
    </figure>
  );
};

export const HubVideos = ({ videos = [], name }) => !videos.length ? null : (
  <section id="watch" className="scroll-mt-24 mb-16" data-testid="hub-videos">
    <HubHead eyebrow="Watch" title={`${name}, live`} />
    <div className="grid md:grid-cols-2 gap-5">
      {videos.map((v, i) => <VideoTile key={`${v.url}-${i}`} v={v} i={i} big={i === 0 && videos.length % 2 === 1} />)}
    </div>
  </section>
);

export const HubPress = ({ press = [] }) => {
  if (!press.length) return null;
  const quotes = press.filter((p) => p.quote), more = press.filter((p) => !p.quote);
  return (
    <section id="press" className="scroll-mt-24 mb-16" data-testid="hub-press">
      <HubHead eyebrow="Press" title="What they're saying" />
      {quotes.map((p, i) => (
        <FadeIn key={i} delay={i * 80}>
          <blockquote className="relative m-0 mb-5 border-l-2 border-[#54E3FF] pl-6 sm:pl-8 py-1 max-w-4xl" data-testid={`hub-press-quote-${i}`}>
            <Quote className="absolute -left-3 -top-1 h-5 w-5 text-[#54E3FF] bg-black" />
            <p className="font-['Playfair_Display'] italic text-xl sm:text-2xl leading-snug text-white m-0">&ldquo;{p.quote}&rdquo;</p>
            <footer className="mt-3 text-xs font-bold uppercase tracking-[0.18em] text-white/55">{p.url ? <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-white/55 hover:text-[#54E3FF] no-underline">{p.source} <ExternalLink className="inline h-3 w-3" /></a> : p.source}</footer>
          </blockquote>
        </FadeIn>
      ))}
      {more.length > 0 && (
        <div className="flex flex-wrap gap-2.5 mt-6">
          {more.map((p, i) => (
            <a key={i} href={p.url || undefined} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-[#54E3FF]/30 hover:border-[#54E3FF] hover:bg-[#54E3FF]/10 px-4 py-2 text-sm font-bold text-white no-underline transition-colors" data-testid={`hub-press-link-${i}`}>{p.source} {p.url && <ExternalLink className="h-3.5 w-3.5 text-[#54E3FF]" />}</a>
          ))}
        </div>
      )}
    </section>
  );
};

const BookBtn = ({ href, icon: Icon, children, testid, solid }) => (
  <a href={href} target={href.startsWith("mailto:") ? undefined : "_blank"} rel="noopener noreferrer" className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-black no-underline transition-[filter,background-color] ${solid ? "bg-[#54E3FF] text-[#030608] hover:brightness-110" : "border border-white/20 text-white hover:bg-white/10"}`} data-testid={testid}><Icon className="h-4 w-4" /> {children}</a>
);

export const HubBooking = ({ band }) => {
  const h = band.hub || {};
  if (!h.booking_note && !h.booking_email && !band.instagram && !band.facebook) return null;
  return (
    <section id="book" className="scroll-mt-24 mb-16" data-testid="hub-booking">
      <div className="rounded-3xl border border-[#54E3FF]/40 bg-[#05090D] p-6 sm:p-10 shadow-[0_0_90px_-30px_rgba(84,227,255,0.55)] grid md:grid-cols-[1.5fr_1fr] gap-8 items-center">
        <div>
          <Eyebrow color={NEON} className="mb-2">For venues &amp; bookers</Eyebrow>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-white m-0">Book {band.name}</h2>
          {h.booking_note && <p className="mt-3 text-base text-white/75 leading-relaxed m-0" data-testid="hub-booking-note">{h.booking_note}</p>}
        </div>
        <div className="flex flex-col gap-2.5">
          {h.booking_email && <BookBtn href={`mailto:${h.booking_email}`} icon={Mail} testid="hub-booking-email" solid>Email {h.booking_email}</BookBtn>}
          {band.instagram && <BookBtn href={band.instagram} icon={Instagram} testid="hub-booking-instagram" solid={!h.booking_email}>Message on Instagram</BookBtn>}
          {band.facebook && <BookBtn href={band.facebook} icon={Facebook} testid="hub-booking-facebook">Message on Facebook</BookBtn>}
        </div>
      </div>
    </section>
  );
};
